import { useRef } from 'react';
import { useSEO, useSchemaLD } from '@/hooks/useSEO';

const benefits = [
  {
    icon: 'ri-scissors-line',
    title: 'Mobiliário que transforma o salão em destino, não só serviço',
    description: 'Clientes escolhem o salão pela experiência. Um ambiente bem projetado — recepção elegante, cadeiras confortáveis, espelhos bem posicionados — faz o cliente voltar e indicar para amigas.',
  },
  {
    icon: 'ri-drop-line',
    title: 'Materiais resistentes a produtos químicos e umidade',
    description: 'MDF revestido em laminado HPL resistente a tinturas, água oxigenada, acetona e produtos de limpeza. Superfícies que não mancham, não descascam e mantêm o visual impecável por anos.',
  },
  {
    icon: 'ri-box-3-line',
    title: 'Projeto 3D gratuito do salão completo',
    description: 'Recepção, área de atendimento, lavatórios e espaço de espera projetados em 3D. Você aprova o layout e os acabamentos antes de fabricar — sem surpresas.',
  },
  {
    icon: 'ri-tools-line',
    title: 'Montagem fora do horário de atendimento',
    description: 'Nossa equipe instala antes da abertura, após o fechamento ou aos domingos. Seu salão não perde um dia de faturamento durante a reforma.',
  },
];

const blocks = [
  {
    title: 'Recepção e Área de Espera que Criam Desejo Antes do Atendimento',
    text: [
      'A cliente entra no salão e já forma a opinião antes de sentar na cadeira. A recepção precisa transmitir sofisticação, organização e cuidado — os mesmos valores que ela espera do serviço. Um balcão bem projetado, cadeiras de espera confortáveis e uma decoração coerente com a identidade do salão fazem a cliente sentir que está no lugar certo.',
      'Nossos balcões de recepção para salões incluem tampo em granito ou laminado HPL resistente a manchas e produtos químicos, frente em MDF com acabamento que pode receber o logo do salão em relevo ou adesivo, espaço interno organizado para computador, agenda e produtos de venda, cadeiras de espera em courvin ou tecido de alta qualidade com estrutura em madeira ou metal, e espelho decorativo com moldura que valoriza o ambiente.',
    ],
    img: '/media/ia/0431.webp',
    reverse: false,
  },
  {
    title: 'Bancadas e Penteadeiras que Organizam e Impressionam',
    text: [
      'A bancada do cabeleireiro é o coração do salão. É onde a cliente passa a maior parte do tempo — olhando para o espelho, para o profissional e para o ambiente ao redor. Uma bancada bem projetada, com espelho bem posicionado, iluminação adequada e organização impecável, transmite profissionalismo e eleva a percepção de valor do serviço.',
      'Projetamos bancadas para salões com tampo em granito ou laminado HPL resistente a tinturas e produtos químicos, gavetas com corrediças telescópicas para organizar ferramentas e produtos, espelhos com moldura em MDF ou alumínio e iluminação LED lateral que elimina sombras no rosto da cliente, nichos e prateleiras para expor produtos de venda, e acabamento em laca fosca ou brilhante nas cores da identidade visual do salão.',
    ],
    img: '/media/ia/0432.webp',
    reverse: true,
  },
  {
    title: 'Área de Lavatório, Spa e Cabines de Estética Completas',
    text: [
      'O lavatório é um momento de relaxamento — e o ambiente precisa reforçar essa sensação. Cadeiras de lavatório confortáveis, armários organizados e revestimento resistente à umidade fazem a diferença entre um lavatório funcional e um momento de spa que a cliente vai comentar com as amigas.',
      'Para a área de lavatório, oferecemos armários em MDF revestido em laminado HPL resistente à umidade, prateleiras para toalhas e produtos, e bancadas com tampo em granito. Para cabines de estética e manicure, mesas de manicure com tampo resistente a acetona e esmalte, cadeiras ergonômicas para a profissional e a cliente, e armários para organizar produtos e equipamentos. Tudo projetado para o ambiente úmido e o uso intenso do salão.',
    ],
    img: '/media/ia/0433.webp',
    reverse: false,
  },
];

const galleryImages = [
  {
    src: '/media/ia/0434.webp',
    alt: 'Recepção elegante de salão de beleza',
  },
  {
    src: '/media/ia/0435.webp',
    alt: 'Bancadas de atendimento com espelhos e iluminação LED',
  },
  {
    src: '/media/ia/0436.webp',
    alt: 'Área de espera de salão com cadeiras elegantes',
  },
  {
    src: '/media/ia/0437.webp',
    alt: 'Mesa de manicure organizada e profissional',
  },
];

const WA_LINK = 'https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Sal%C3%B5es%20de%20Beleza%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!';

export default function BelezaEsteticaPage() {
  const whatsappBtnRef = useRef<HTMLAnchorElement>(null);

  useSEO({
    title: 'Mobiliário para Salão de Beleza Caruaru PE | Estética – Home Office Premium',
    description: 'Mobiliário para salões de beleza, barbearias e clínicas estéticas em Caruaru PE. Recepção, bancadas, lavatórios e cabines. Materiais resistentes a produtos químicos, projeto 3D gratuito e montagem fora do horário de atendimento. Atendemos salões no Agreste e Sertão de Pernambuco.',
    keywords: 'mobiliário salão beleza Recife, móveis barbearia Petrolina, bancada salão Garanhuns, recepção salão Caruaru, mobiliário estética Agreste PE, penteadeira salão Pernambuco, móveis salão Nordeste, bancada cabeleireiro Recife, mobiliário barbearia Petrolina, móveis salão Caruaru',
    path: '/beleza-estetica',
  });

  useSchemaLD({
    id: 'schema-beleza-estetica',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Mobiliário para Salões de Beleza e Estética | Caruaru PE',
      description: 'Mobiliário para salões de beleza, barbearias e clínicas estéticas: recepção, bancadas, lavatórios e cabines. Projeto 3D gratuito, equipe própria de montagem.',
      url: `${import.meta.env.VITE_SITE_URL ?? 'https://homeofficepremium.com'}/beleza-estetica`,
      about: {
        '@type': 'Service',
        name: 'Mobiliário para Salões de Beleza e Estética',
        provider: {
          '@type': 'Organization',
          name: 'Home Office Premium',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Av. Agamenon Magalhães, 1519',
            addressLocality: 'Caruaru',
            addressRegion: 'PE',
            postalCode: '55014-000',
            addressCountry: 'BR',
          },
        },
      },
    },
  });

  return (
    <div className="lp-page-wrapper min-h-screen bg-white">
      {/* Hero */}
      <section
        className="relative min-h-[640px] md:min-h-[700px] flex items-center"
        style={{
          backgroundImage: 'url(/media/ia/0438.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/20"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-28 pb-16 w-full">
          <div className="max-w-2xl">
            <span className="inline-block bg-[#E8610A] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-5">
              Salões de Beleza · Barbearias · Estética
            </span>
            <h1 className="text-white text-4xl md:text-5xl font-bold leading-tight mb-4">
              Mobiliário que transforma seu salão em destino — não só serviço.
            </h1>
            <p className="text-white/85 text-lg leading-relaxed mb-3">
              Recepção elegante, bancadas que impressionam e lavatórios que parecem spa. Materiais resistentes a tinturas e produtos químicos — porque beleza exige durabilidade.
            </p>
            <p className="text-white/70 text-sm mb-8">
              Projeto 3D gratuito · Montagem fora do horário de atendimento · Suporte local em Caruaru e Agreste
            </p>
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-8 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              SOLICITAR ORÇAMENTO E PROJETO 3D GRATUITO
            </a>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-bold">
              Por que salões de Caruaru escolhem a <span className="text-[#E8610A]">Home Office Premium</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b) => (
              <div key={b.title} className="bg-white rounded-xl p-6 flex flex-col gap-4 border border-gray-200">
                <div className="w-12 h-12 flex items-center justify-center bg-[#E8610A]/10 rounded-lg">
                  <i className={`${b.icon} text-[#E8610A] text-2xl`}></i>
                </div>
                <h3 className="text-gray-900 font-bold text-base leading-snug">{b.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Content Blocks */}
      <section className="py-16 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-normal leading-tight">
              <span className="text-[#E8610A] font-semibold">Ambientes de Beleza</span> que Encantam e Fidelizam Clientes
            </h2>
            <div className="w-16 h-0.5 bg-[#E8610A] mx-auto mt-4"></div>
          </div>
          <div className="flex flex-col gap-12">
            {blocks.map((block, idx) => (
              <div key={idx} className={`flex flex-col ${block.reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-8 bg-white rounded-xl p-8 border border-gray-200`}>
                <div className="w-full lg:w-1/2 h-64 md:h-72 rounded-lg overflow-hidden flex-shrink-0">
                  <img src={block.img} alt={block.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="w-full lg:w-1/2">
                  <h3 className="text-gray-900 text-xl font-bold mb-4 leading-snug">{block.title}</h3>
                  <div className="flex flex-col gap-3">
                    {block.text.map((p, i) => (
                      <p key={i} className="text-gray-600 text-sm leading-relaxed">{p}</p>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-10">
            <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-10 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide">
              <i className="ri-whatsapp-line text-lg"></i>
              SOLICITAR ORÇAMENTO E PROJETO 3D GRATUITO
            </a>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 bg-[#1a1a1a]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-[#E8610A] text-sm font-bold uppercase tracking-widest mb-4">Sem custo e sem compromisso</p>
          <h2 className="text-white text-3xl md:text-4xl font-bold leading-tight mb-4">
            Veja seu salão completo em 3D antes de investir.
          </h2>
          <p className="text-gray-400 text-base mb-10">
            Recepção, bancadas, lavatórios e cabines projetados em 3D. Você aprova cada detalhe antes de fabricarmos. Montagem fora do horário de atendimento — sem perder um dia de faturamento.
          </p>
          <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide">
            <i className="ri-whatsapp-line text-lg"></i>
            QUERO MEU PROJETO 3D GRATUITO
          </a>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16 bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-gray-400 text-sm mb-2">Galeria</p>
            <h2 className="text-white text-2xl md:text-3xl font-bold">
              Ambientes de <span className="text-[#E8610A]">Beleza e Estética Premium</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {galleryImages.map((img, idx) => (
              <div key={idx} className="w-full h-64 md:h-80 rounded-xl overflow-hidden">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">SUPORTE LOCAL · CARUARU E AGRESTE PERNAMBUCANO</p>
          <h2 className="text-gray-900 text-4xl md:text-5xl font-bold mb-4">Solicite seu orçamento</h2>
          <p className="text-gray-500 text-base mb-8">Projeto 3D gratuito incluso. Resposta em até 2 horas.</p>
          <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 justify-center bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm mb-10">
            <i className="ri-whatsapp-line text-lg"></i>
            Chamar no WhatsApp
          </a>
          <div className="mt-10 text-gray-500 text-sm">
            <p>Av. Agamenon Magalhães, 1519 · Maurício de Nassau, Caruaru - PE · CEP 55014-000</p>
          </div>
        </div>
      </section>

      <footer className="bg-white py-8 border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-6 flex flex-col items-center gap-4">
          <img src="https://files.greatpages.com.br/arquivos/paginas_editor/98015-32d41c6ba8b126a2913863e7b342fb04.png" alt="Home Office Premium" className="h-16 w-auto object-contain" />
          <div className="flex items-center gap-4">
            <a href="https://www.instagram.com/homeoffice.premium/" target="_blank" rel="noopener noreferrer" className="cursor-pointer"><img src="https://files.greatpages.com.br/arquivos/paginas_editor/1-cc1918b73804ac63dbfe7624bf53ae73.png" alt="Instagram" className="w-8 h-8 object-contain" /></a>
            <a href="https://www.facebook.com/homeoffice.premium" target="_blank" rel="noopener noreferrer" className="cursor-pointer"><img src="https://files.greatpages.com.br/arquivos/paginas_editor/1-d39cee63d39805fee1ea3fe03b5a2e09.png" alt="Facebook" className="w-8 h-8 object-contain" /></a>
          </div>
        </div>
      </footer>

      <a ref={whatsappBtnRef} href={WA_LINK} target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-[9999] w-14 h-14 flex items-center justify-center bg-[#25D366] rounded-full cursor-pointer hover:scale-110 transition-transform duration-200" aria-label="WhatsApp" style={{ display: 'flex' }}>
        <i className="ri-whatsapp-line text-white text-2xl"></i>
      </a>
    </div>
  );
}
